import {
  AutoPlayType,
  AutoplayTags,
  BASE_PCS_API_URL,
  VideoTags
} from "../../globals";

export function getAutoplayType(video, forceNoLoop = false) {
  // videos should only be given one tag related to autoplaytype
  // if a video is accidentally given multiple tags, the order of precedence should be 1) looping-promo/looping-no-controls, 2) looping-video, 3) autoplay
  const tags = video?.taxonomy?.tags?.map(x => x.text);

  // check video tags for 'looping-promo', or 'looping-no-controls'
  const isTypeLoopNoControls =
    !forceNoLoop &&
    [AutoplayTags.LOOPING_PROMO, AutoplayTags.LOOPING_NO_CONTROLS].some(x =>
      tags?.includes(x)
    );
  if (isTypeLoopNoControls) return AutoPlayType.LOOP_NO_CONTROLS;

  // check video tags for 'looping-video'
  const isTypeLoop = !forceNoLoop && tags?.includes(AutoplayTags.LOOPING_VIDEO);
  if (isTypeLoop) return AutoPlayType.LOOP;

  // Check video tags for 'autoplay'
  const isTypeAutoplay = tags?.includes(AutoplayTags.AUTOPLAY);
  if (isTypeAutoplay) return AutoPlayType.AUTO;

  return null;
}

export function isLoopingVideo(video) {
  const autoplayType = getAutoplayType(video);
  return (
    autoplayType === AutoPlayType.LOOP ||
    autoplayType === AutoPlayType.LOOP_NO_CONTROLS
  );
}

/**
 * Overrides the tags on the video ANS if debug tags are passed from storybook.
 *
 * @param {string[]} debugTags - An array of debug tags to be added to the video.
 * @param {object} video - The video object to which the debug tags are to be added.
 * @returns {void}
 */
export function addTagsToVideo(debugTags = [], video) {
  if (video?.taxonomy) {
    // If debugTags are empty, use the video tags as source of truth.
    if (debugTags.length === 0) {
      debugTags = video.taxonomy?.tags?.map(x => x.text) || [];
    } else {
      const existingTags = new Set(
        video.taxonomy?.tags?.map(x => x.text) || []
      );
      const tagsToAdd = debugTags.filter(x => !existingTags.has(x));

      video.taxonomy = video.taxonomy || {};
      video.taxonomy.tags = [
        ...(video.taxonomy.tags || []),
        ...tagsToAdd.map(x => ({ text: x }))
      ];
    }
  }
}

export const getDomain = domainOverride => {
  return domainOverride
    ? domainOverride
    : typeof window !== "undefined" && window?.location?.hostname
    ? window.location.hostname
    : "washingtonpost.com";
};

/**
 * Adds an Orca event to the player's events array.
 *
 * @param {string} eventName - The name of the event to add.
 * @param {object} eventData - The data to add to the event.
 * @param {string} videoID - The ID of the video player.
 * @returns {void}
 */
export function addOrcaEvent(eventName, eventData, videoID, debug = false) {
  if (debug) {
    console.log(eventName, eventData);
  }
  if (window?.orcaPlayers) {
    const player = window.orcaPlayers[videoID];
    if (player) {
      player.events.push({ [eventName]: eventData });
    }
  }
}

export const getIsStickyEnabled = (autoplayState, config) => {
  const { isSticky = true, isLead = false } = config;
  const allowSticky =
    // Looping video should never go sticky
    autoplayState.isLooping ||
    // Autoplaying video should not go sticky UNLESS in lead spot
    (autoplayState.type === AutoPlayType.AUTO && !isLead)
      ? false
      : isSticky;

  return allowSticky;
};

export const hasBurnedHeadline = video => {
  const tagsArray = video?.taxonomy?.tags;
  return (
    tagsArray?.filter(tag => tag.text === VideoTags.BURNED_HEADLINE).length > 0
  );
};

export const hasBurnedCaptions = video => {
  const tagsArray = video?.taxonomy?.tags;
  return (
    tagsArray?.filter(tag => tag.text === VideoTags.BURNED_CAPTIONS).length > 0
  );
};

export const getLiveConfigUrl = liveConfigEnv => {
  liveConfigEnv = liveConfigEnv.replace(/[^a-z0-9\s-]/gi, "");
  return `https://d2p9l91d5g68ru.cloudfront.net/LiveConfig/${liveConfigEnv}.json`;
};

export function isFullVideoData(video) {
  return !!(video && video.streams && video.streams[0]?.url);
}

export function getVideoApiUrl(isSandbox) {
  const baseUrl = isSandbox ? BASE_PCS_API_URL.SANDBOX : BASE_PCS_API_URL.PROD;
  return `${baseUrl}/video-center-find-by-uuid`;
}

/**
 * Builds a PCS video-center-find-by-uuid request URL with a JSON `query` param.
 *
 * @param {string} videoApiUrl - PCS find-by-uuid endpoint from getVideoApiUrl
 * @param {object} options
 * @param {string} options.uuid - Video UUID
 * @param {string} [options.domain] - Site domain (included when provided)
 * @returns {URL}
 */
export function buildPcsFindByUuidUrl(
  videoApiUrl,
  { uuid, domain, ...queryOverrides } = {}
) {
  try {
    const url = new URL(videoApiUrl);
    const query = {
      uuid,
      unpublished: false,
      includePubEnded: false,
      ...queryOverrides
    };

    if (domain) {
      query.domain = domain;
    }

    url.searchParams.set("query", JSON.stringify(query));
    return url;
  } catch (error) {
    console.error(
      `Error building PCS find-by-uuid URL for ${videoApiUrl}`,
      error
    );
    return null;
  }
}

/**
 * Fetches video ANS data from the PCS find-by-uuid endpoint.
 *
 * @param {string} videoApiUrl
 * @param {object} queryOptions - Passed to buildPcsFindByUuidFetchUrl
 * @param {RequestInit} [fetchOptions]
 * @returns {Promise<Array|null>}
 */
export async function fetchPcsVideosByUuid(
  videoApiUrl,
  queryOptions,
  fetchOptions = {}
) {
  const url = buildPcsFindByUuidUrl(videoApiUrl, queryOptions);

  try {
    const response = await fetch(url, fetchOptions);
    const data = await response.json();

    if (!Array.isArray(data)) {
      //console.error("PCS video request returned an error:", data);
      return null;
    }

    return data;
  } catch (error) {
    //console.error("Error retrieving video data", error);
    return null;
  }
}

export const getIsVertical = video => {
  return (
    video &&
    (video.additional_properties?.vertical ||
      video.additional_properties?.videoCategory === "vertical" ||
      false)
  );
};

export const getInitDuration = video => {
  return video.duration / 1000 || 0;
};

const isHttpUrl = str => /^https?:\/\//.test(str);

function getEmbedHostname(
  embedDomain = window.self !== window.top
    ? document.referrer
    : window.location.href
) {
  try {
    if (!isHttpUrl(embedDomain)) return "";

    embedDomain = embedDomain.split("/")[2];
    embedDomain = embedDomain.split(":")[0];
  } catch (e) {
    console.log(e);
  }

  return embedDomain;
}

function getEmbedDomain(
  embedDomain = window.self !== window.top
    ? document.referrer
    : window.location.href
) {
  try {
    embedDomain = getEmbedHostname(embedDomain);

    if (embedDomain) {
      const parts = embedDomain.split(".");
      const { length } = parts;

      // depth is one greater than the TLD
      let depth = parts[length - 2] === "co" ? 3 : 2;

      if (length >= depth) {
        const chunks = [];
        while (depth) {
          chunks.push(parts[length - depth]);
          depth--;
        }
        embedDomain = chunks.join(".");
      }
    }
  } catch (e) {
    console.log(e);
  }

  return embedDomain;
}

export function isWapoDomain(
  href = window.self !== window.top ? document.referrer : window.location.href
) {
  const testDomain = domain => {
    return /washingtonpost\.com|localhost|thelily\.com|arcpublishing\.com|wpprivate\.com|wapo\.st|wapo\.pub|github\.io|zeustechnology\.com|zeus\.aws\.arc\.pub/.test(
      domain
    );
  };
  const embedDomain = getEmbedDomain(href); // the last two chunks of the domain
  if (testDomain(embedDomain) || testDomain(href)) return true;

  const embedHostname = getEmbedHostname(href); // the full hostname
  if (
    /www-washingtonpost-com\.cdn\.ampproject\.org|wpit\.nile\.works|washpost\.arcpublishing\.com/.test(
      embedHostname
    )
  )
    return true;

  return false;
}
